import React, { useState, useEffect, useMemo } from "react";
import { ref, onValue, off } from "firebase/database";
import { database } from "../../../firebaseConfig";
import { Store, Plus, Edit, Eye, Truck, TrendingUp } from "lucide-react";
import AddShopModal from "./AddShopModal";
import EditShopModal from "./EditShopModal";
import ViewShopModal from "./ViewShopModal";
import RecordShopDeliveryModal from "./RecordShopDeliveryModal";

const ShopTable = ({ companyName }) => {
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedShop, setSelectedShop] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeliveryModal, setShowDeliveryModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (!companyName) return;

    const shopsRef = ref(database, `Companies/${companyName}/Shops`);

    onValue(shopsRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        const shopsList = Object.entries(data).map(([id, shop]) => ({
          id,
          ...shop,
        }));
        setShops(shopsList);
      } else {
        setShops([]);
      }
      setLoading(false);
    });

    return () => {
      off(shopsRef);
    };
  }, [companyName]);

  const filteredShops = useMemo(() => {
    if (!searchTerm.trim()) return shops;
    const lower = searchTerm.toLowerCase();
    return shops.filter(
      (shop) =>
        shop.shopName?.toLowerCase().includes(lower) ||
        shop.location?.toLowerCase().includes(lower) ||
        shop.managerName?.toLowerCase().includes(lower)
    );
  }, [searchTerm, shops]);

  const totalInventory = shops.reduce((sum, shop) => sum + (parseFloat(shop.currentStock) || 0), 0);
  const totalSales = shops.reduce((sum, shop) => sum + (parseFloat(shop.totalSales) || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10">
        <p className="text-gray-600">Loading shops...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with Actions */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-cyan-700 flex items-center gap-2">
            <Store size={28} />
            Shop Management
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Manage your company's retail shops and track milk deliveries
          </p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="bg-cyan-600 text-white px-4 py-2 rounded-lg hover:bg-cyan-700 transition flex items-center gap-2"
        >
          <Plus size={20} />
          Add New Shop
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white border rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-gray-600">Total Shops</p>
            <Store className="text-cyan-600" size={20} />
          </div>
          <p className="text-2xl font-bold text-cyan-700">{shops.length}</p>
        </div>
        <div className="bg-white border rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-gray-600">Total Stock (Liters)</p>
            <Truck className="text-blue-600" size={20} />
          </div>
          <p className="text-2xl font-bold text-blue-700">{totalInventory.toLocaleString()}</p>
        </div>
        <div className="bg-white border rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-gray-600">Total Sales (KES)</p>
            <TrendingUp className="text-green-600" size={20} />
          </div>
          <p className="text-2xl font-bold text-green-700">{totalSales.toLocaleString()}</p>
        </div>
      </div>

      {/* Search Bar */}
      <div className="flex justify-between items-center">
        <input
          type="text"
          placeholder="Search shops by name, location, or manager..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="border px-4 py-2 rounded-lg w-96 focus:outline-none focus:ring-2 focus:ring-cyan-600"
        />
      </div>

      {/* Shops Table */}
      {filteredShops.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm p-8 text-center">
          <Store className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 mb-4">
            {shops.length === 0
              ? "No shops registered yet. Click 'Add New Shop' to get started."
              : "No shops match your search criteria."}
          </p>
          {shops.length === 0 && (
            <button
              onClick={() => setShowAddModal(true)}
              className="bg-cyan-600 text-white px-6 py-2 rounded-lg hover:bg-cyan-700 transition"
            >
              Add Your First Shop
            </button>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <table className="min-w-full">
            <thead className="bg-cyan-600 text-white">
              <tr>
                <th className="text-left py-3 px-4">Shop Name</th>
                <th className="text-left py-3 px-4">Location</th>
                <th className="text-left py-3 px-4">Manager</th>
                <th className="text-left py-3 px-4">Phone</th>
                <th className="text-left py-3 px-4">Current Stock (L)</th>
                <th className="text-left py-3 px-4">Status</th>
                <th className="text-center py-3 px-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredShops.map((shop, index) => (
                <tr
                  key={shop.id}
                  className={`border-t hover:bg-cyan-50 transition ${
                    index % 2 === 0 ? "bg-white" : "bg-gray-50"
                  }`}
                >
                  <td className="py-3 px-4 font-medium text-gray-800">
                    {shop.shopName || "N/A"}
                  </td>
                  <td className="py-3 px-4 text-gray-600">
                    {shop.location || "N/A"}
                  </td>
                  <td className="py-3 px-4 text-gray-600">
                    {shop.managerName || "N/A"}
                  </td>
                  <td className="py-3 px-4 text-gray-600">
                    {shop.phoneNumber || "N/A"}
                  </td>
                  <td className="py-3 px-4 font-semibold text-gray-700">
                    {parseFloat(shop.currentStock || 0).toLocaleString()}
                  </td>
                  <td className="py-3 px-4">
                    <span
                      className={`px-3 py-1 rounded-full text-sm font-medium ${
                        shop.status === "Active"
                          ? "bg-green-100 text-green-700"
                          : "bg-yellow-100 text-yellow-700"
                      }`}
                    >
                      {shop.status || "Active"}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-center space-x-2">
                    <button
                      onClick={() => {
                        setSelectedShop(shop);
                        setShowViewModal(true);
                      }}
                      className="bg-cyan-600 text-white px-3 py-1 rounded-md hover:bg-cyan-700 text-sm inline-flex items-center gap-1"
                    >
                      <Eye size={14} />
                      View
                    </button>
                    <button
                      onClick={() => {
                        setSelectedShop(shop);
                        setShowEditModal(true);
                      }}
                      className="bg-amber-500 text-white px-3 py-1 rounded-md hover:bg-amber-600 text-sm inline-flex items-center gap-1"
                    >
                      <Edit size={14} />
                      Edit
                    </button>
                    <button
                      onClick={() => {
                        setSelectedShop(shop);
                        setShowDeliveryModal(true);
                      }}
                      className="bg-green-600 text-white px-3 py-1 rounded-md hover:bg-green-700 text-sm inline-flex items-center gap-1"
                    >
                      <Truck size={14} />
                      Deliver
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Info Section */}
      <div className="bg-cyan-50 border border-cyan-100 rounded-lg p-4">
        <p className="text-sm text-cyan-800">
          💡 <strong>Tip:</strong> Use shops to track milk deliveries and sales at your retail locations.
          Record deliveries to update stock levels, and monitor sales performance across all shops.
        </p>
      </div>

      {/* Modals */}
      {showAddModal && (
        <AddShopModal
          companyName={companyName}
          onClose={() => setShowAddModal(false)}
        />
      )}
      {showEditModal && selectedShop && (
        <EditShopModal
          shop={selectedShop}
          companyName={companyName}
          onClose={() => setShowEditModal(false)}
        />
      )}
      {showViewModal && selectedShop && (
        <ViewShopModal
          shop={selectedShop}
          companyName={companyName}
          onClose={() => setShowViewModal(false)}
        />
      )}
      {showDeliveryModal && selectedShop && (
        <RecordShopDeliveryModal
          shop={selectedShop}
          companyName={companyName}
          onClose={() => setShowDeliveryModal(false)}
        />
      )}
    </div>
  );
};

export default ShopTable;
